import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet';

const styles = {
  welcomeCard: {
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto",
  },
  card: {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    padding: "40px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease"
  },
  welcomeTitle: {
    fontSize: "32px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: "20px",
    textAlign: "center",
  },
  welcomeText: {
    color: "#64748b",
    marginBottom: "40px",
    lineHeight: "1.7",
    fontSize: "18px",
    textAlign: "center",
    maxWidth: "600px",
    margin: "0 auto 40px auto",
  },
  toolGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px",
    marginTop: "40px",
  },
  toolCard: {
    padding: "28px 20px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    textAlign: "center",
    transform: "translateY(0)",
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
    position: "relative",
    overflow: "hidden",
    textDecoration: "none",
    color: "#1e293b",
  },
  toolIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    display: "block",
    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
    transition: "transform 0.3s ease"
  },
  toolName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "8px",
  },
  toolDescription: {
    fontSize: "14px",
    color: "#64748b",
    lineHeight: "1.5"
  },
};

const tools = [
  { path: "/todo", name: "Calendar To-do List", icon: "📝", description: "Manage your tasks" },
  { path: "/calculator", name: "Marks Calculator", icon: "📊", description: "Calculate grades" },
  { path: "/budget", name: "Budget Manager", icon: "💰", description: "Track expenses" },
  { path: "/timer", name: "Countdown Timer", icon: "⏰", description: "Time management" },
];

export default function Home() {
  const [hoveredTool, setHoveredTool] = useState(null);

  const getToolCardStyle = (toolPath) => ({
    ...styles.toolCard,
    ...(hoveredTool === toolPath ? {
      transform: 'translateY(-8px)',
      boxShadow: '0 20px 40px rgba(102, 126, 234, 0.2)',
      borderColor: 'rgba(102, 126, 234, 0.3)',
      background: 'rgba(255, 255, 255, 1)'
    } : {})
  });

  return (
    <>
      <Helmet>
        <title>Student Tools Hub - Free Academic Productivity Tools</title>
        <meta name="description" content="Free student productivity tools including todo lists, grade calculator, budget manager, and study timers. Boost your academic success with our comprehensive toolkit." />
        <meta name="keywords" content="student tools, academic planner, grade calculator, budget tracker, study timer, productivity, college tools" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Student Tools Hub - Free Academic Productivity Tools" />
        <meta property="og:description" content="Free student productivity tools including todo lists, grade calculator, budget manager, and study timers." />
        <meta property="og:image" content="https://student-tools-hub.vercel.app/logo.png" />
        <meta property="og:url" content="https://student-tools-hub.vercel.app/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Student Tools Hub - Free Academic Productivity Tools" />
        <meta name="twitter:description" content="Free student productivity tools for academic success" />
        <meta name="twitter:image" content="https://student-tools-hub.vercel.app/logo.png" />
      </Helmet>
    <div style={styles.welcomeCard}>
      <div style={styles.card}>
        <h2 style={styles.welcomeTitle}>Welcome to Student Tools Hub!</h2>
        <p style={styles.welcomeText}>
          🚀 Boost your productivity with our comprehensive suite of student tools. 
          From task management to grade calculation, everything you need for academic success is here.
        </p>
        <div style={styles.toolGrid}>
          {tools.map((tool) => (
            <Link 
              key={tool.path} 
              to={tool.path} 
              style={getToolCardStyle(tool.path)}
              onMouseEnter={() => setHoveredTool(tool.path)}
              onMouseLeave={() => setHoveredTool(null)}
            >
              <div style={styles.toolIcon}>{tool.icon}</div>
              <div style={styles.toolName}>{tool.name}</div>
              <div style={styles.toolDescription}>{tool.description}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}